import React, { useEffect, useRef, useState, useMemo } from 'react';
import { X, Maximize2, Minimize2, Send, Sparkles, Loader2, Paperclip, XCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import ChatService from '../services/chatService';
import KnowledgeService from '../services/knowledgeService';

const STORAGE_KEY = 'prezlab-ai-chat-key';
const MAX_CHAT_ATTACHMENTS = 5;
const MAX_ATTACHMENT_BYTES = 4 * 1024 * 1024; // 4 MB

const formatTimestamp = (date) =>
  date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

export function AIChat({ isOpen, onClose, isExpanded, onToggleExpand, analysis, projects = [], projectAnalyses = {} }) {
  const knowledgeServiceRef = useRef(new KnowledgeService());
  const apiKey = useMemo(() => process.env.REACT_APP_OPENAI_API_KEY || localStorage.getItem(STORAGE_KEY) || '', []);
  const [chatService, setChatService] = useState(null);
  const [messages, setMessages] = useState(() => [
    {
      id: 'welcome',
      role: 'assistant',
      content: 'Ask anything about your presentation or design principles. I can reference the Design Compass analysis, knowledge base articles, and best practices to help you.',
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const [hasUserSentMessage, setHasUserSentMessage] = useState(false);
  const [referenceProjectId, setReferenceProjectId] = useState(null);
  const fileInputRef = useRef(null);
  const [attachments, setAttachments] = useState([]);
  useEffect(() => {
    if (apiKey) {
      localStorage.setItem(STORAGE_KEY, apiKey);
      setChatService(new ChatService(apiKey, knowledgeServiceRef.current));
    } else {
      setChatService(null);
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [apiKey]);

  // Load knowledge from Supabase when chat opens
  useEffect(() => {
    if (isOpen && knowledgeServiceRef.current) {
      // Load asynchronously without blocking - don't await
      setTimeout(() => {
        knowledgeServiceRef.current.loadSupabaseKnowledge().catch(err => {
          console.error('Failed to load knowledge:', err);
        });
      }, 100);
    }
  }, [isOpen]);

  // Get reference analysis if a reference project is selected
  const referenceAnalysis = referenceProjectId ? projectAnalyses[referenceProjectId]?.analysis : null;
  const activeAnalysis = referenceAnalysis || analysis;

  useEffect(() => {
    if (chatService) {
      setSuggestedQuestions(chatService.suggestQuestions(activeAnalysis));
    } else {
      setSuggestedQuestions([]);
    }
  }, [chatService, activeAnalysis]);

  const clearAttachments = () => {
    setAttachments([]);
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) {
      return;
    }

    let next = [...attachments];
    let feedback = '';
    for (const file of files) {
      if (next.length >= MAX_CHAT_ATTACHMENTS) {
        feedback = `You can attach up to ${MAX_CHAT_ATTACHMENTS} images.`;
        break;
      }
      if (!file.type.startsWith('image/')) {
        feedback = 'Only image files are supported.';
        continue;
      }
      if (file.size > MAX_ATTACHMENT_BYTES) {
        feedback = `Image ${file.name} exceeds the 4 MB limit.`;
        continue;
      }
      const previewUrl = URL.createObjectURL(file);
      next = [...next, { file, previewUrl, id: `${file.name}-${Date.now()}-${Math.random()}` }];
    }
    setAttachments(next);
    if (feedback) {
      setError(feedback);
    }
    if (event.target) {
      event.target.value = '';
    }
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => {
      const next = [...prev];
      const [removed] = next.splice(index, 1);
      if (removed?.previewUrl) {
        URL.revokeObjectURL(removed.previewUrl);
      }
      return next;
    });
  };

  const handleSend = async (question, attachmentOverride) => {
    const rawMessage = question !== undefined ? question : inputValue;
    const messageText = (rawMessage || "").trim();
    if (!messageText) {
      return;
    }
    if (!chatService) {
      setError('The assistant is currently unavailable.');
      return;
    }

    const activeAttachments = Array.isArray(attachmentOverride) ? attachmentOverride : attachments;
    const messageAttachments = activeAttachments.map((item, index) => ({
      previewUrl: item?.previewUrl || '',
      name: item?.file?.name || `Image ${index + 1}`,
      id: item?.id || `${Date.now()}-${index}`
    }));
    const attachmentFiles = activeAttachments
      .map((item) => item?.file)
      .filter(Boolean);

    setError('');
    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: messageText,
      timestamp: new Date(),
      attachments: messageAttachments,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setHasUserSentMessage(true); // Hide quick questions after first message
    setIsLoading(true);

    try {
      const history = [...messages, userMessage].map((msg) => ({
        role: msg.role,
        content:
          msg.content +
          (msg.attachments?.length
            ? `

(Attached images: ${msg.attachments.map((att) => att.name).join(', ')})`
            : ''),
      }));

      // Create a temporary assistant message for streaming
      const assistantMessageId = `assistant-${Date.now()}`;
      const assistantMessage = {
        id: assistantMessageId,
        role: 'assistant',
        content: 'Thinking...',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMessage]);

      // Use streaming with real-time updates
      await chatService.sendMessage(
        messageText,
        activeAnalysis,
        history,
        (delta, aggregated) => {
          // Update the assistant message content in real-time
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantMessageId
                ? { ...msg, content: aggregated }
                : msg
            )
          );
        },
        attachmentFiles
      );

      if (attachmentOverride === undefined) {
        clearAttachments();
      }

      setSuggestedQuestions(chatService.suggestQuestions(analysis));
    } catch (err) {
      console.error('Chat error', err);
      setError(err.message || 'Unable to get a response right now.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={`fixed z-50 bg-card/95 backdrop-blur-lg border border-border/50 rounded-2xl shadow-2xl transition-all duration-300 flex flex-col ${isExpanded ? 'inset-8' : 'bottom-8 right-8 w-[440px] h-[560px]'} `}
    >
      <div className="flex items-center justify-between p-5 border-b border-border/30 bg-gradient-to-r from-accent/10 to-accent/5 rounded-t-2xl">
        <div className="flex items-center space-x-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-r from-accent to-accent/80 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-accent-foreground" />
          </div>
          <div>
            <h3 className="font-bold text-foreground">AI Assistant</h3>
            <p className="text-xs text-muted-foreground">
              {analysis ? 'Context: latest analysis connected' : 'Run an analysis to unlock deeper insights'}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="sm" className="h-8 w-8" onClick={onToggleExpand}>
            {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="border-b border-border/30 bg-card/70 p-4 space-y-3">
        {/* Reference Project Selector */}
        {projects.length > 0 && (
          <div className="rounded-lg border border-border/50 bg-background/40 p-3">
            <label className="text-xs font-semibold text-muted-foreground uppercase block mb-2">
              Reference Project (Optional)
            </label>
            <select
              value={referenceProjectId || ''}
              onChange={(e) => setReferenceProjectId(e.target.value || null)}
              className="w-full text-sm bg-input border border-border rounded-md px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
            >
              <option value="">Current analysis only</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name} {project.slideCount ? `(${project.slideCount} slides)` : ''}
                </option>
              ))}
            </select>
            {referenceProjectId && (
              <p className="mt-2 text-xs text-muted-foreground">
                ✓ Using {projects.find(p => p.id === referenceProjectId)?.name} as reference
              </p>
            )}
          </div>
        )}

        {activeAnalysis && (
          <div className="rounded-lg border border-border/50 bg-background/40 p-3 text-xs text-muted-foreground">
            <p className="font-semibold text-foreground">{activeAnalysis.presentationType?.primary || 'Presentation analysis ready'}</p>
            <p>{activeAnalysis.contextualGrounding?.identifiedObjective || 'Objective pending'}</p>
          </div>
        )}
        {error && (
          <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-2 text-xs text-red-300">
            {error}
          </div>
        )}
      </div>

      <div className={`flex-1 overflow-y-auto ${isExpanded ? 'p-6' : 'p-4'}`}>
        <div className="space-y-4">
          {messages.map((message) => (
            <div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 shadow-sm border ${message.role === 'user'
                  ? 'bg-gradient-to-r from-accent to-accent/80 text-accent-foreground border-accent/60'
                  : 'bg-muted/50 text-foreground border-border/40'
                  }`}
              >
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
                {message.attachments?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {message.attachments.map((attachment, attachmentIndex) =>
                      attachment.previewUrl ? (
                        <img
                          key={attachment.id || attachmentIndex}
                          src={attachment.previewUrl}
                          alt={attachment.name || `Attachment ${attachmentIndex + 1}`}
                          className="h-16 w-16 rounded-lg border border-border/30 object-cover"
                        />
                      ) : null
                    )}
                  </div>
                )}
                <span className="mt-2 block text-xs opacity-70">{formatTimestamp(message.timestamp)}</span>
              </div>
            </div>
          ))}
          {!activeAnalysis && (
            <div className="rounded-xl border border-border/40 bg-background/40 p-4 text-xs text-muted-foreground">
              Run a Design Compass analysis to give the assistant full slide-by-slide context.
            </div>
          )}
        </div>
      </div>

      {attachments.length > 0 && (
        <div className="px-4 pb-2">
          <div className="flex flex-wrap gap-3">
            {attachments.map((attachment, index) => (
              <div key={attachment.id || index} className="relative">
                <img
                  src={attachment.previewUrl}
                  alt={attachment.file?.name || `Attachment ${index + 1}`}
                  className="h-16 w-16 rounded-lg border border-border/40 object-cover"
                />
                <button
                  type="button"
                  onClick={() => removeAttachment(index)}
                  className="absolute -top-2 -right-2 bg-background/90 rounded-full p-1 text-muted-foreground shadow hover:text-destructive"
                  aria-label="Remove attachment"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {!hasUserSentMessage && suggestedQuestions.length > 0 && (
        <div className="px-4 pb-2">
          <div className="rounded-xl border border-border/40 bg-card/60 p-3 space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase">Quick questions</p>
            <div className="flex flex-wrap gap-2">
              {suggestedQuestions.slice(0, 4).map((question, index) => (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  onClick={() => handleSend(question, [])}
                >
                  {question.length > 60 ? `${question.slice(0, 57)}...` : question}
                </Button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div
        className="p-4 border-t border-border/30 bg-card/70 rounded-b-2xl"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const files = Array.from(e.dataTransfer.files || []);
          if (files.length > 0) {
            const dt = new DataTransfer();
            files.forEach(f => dt.items.add(f));
            handleFileSelect({ target: { files: dt.files } });
          }
        }}
      >
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleFileSelect}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-10 w-10"
            onClick={() => fileInputRef.current?.click()}
            disabled={!chatService || isLoading}
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          <Input
            value={inputValue}
            onChange={(event) => setInputValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                handleSend();
              }
            }}
            onPaste={(e) => {
              const items = Array.from(e.clipboardData?.items || []);
              const imageItem = items.find(item => item.type.startsWith('image/'));
              if (imageItem) {
                e.preventDefault();
                const file = imageItem.getAsFile();
                if (file) {
                  const dt = new DataTransfer();
                  dt.items.add(file);
                  handleFileSelect({ target: { files: dt.files } });
                }
              }
            }}
            placeholder="Ask about colours, layout, narrative, or anything else?"
            disabled={!chatService || isLoading}
            className="flex-1 bg-input border-border"
          />
          <Button
            type="button"
            onClick={() => handleSend()}
            disabled={!chatService || isLoading}
            className="h-10 w-10 bg-gradient-to-r from-accent to-accent/80 text-accent-foreground"
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}




